<template>
  <div>
    <v-card class="px-8 py-8">
      <v-form ref="extraForm">
        <v-row>
          <v-col cols="12" class="pb-8 pt-0">
            <div>
              <h2 class="font-weight-regular pb-4">Details <span class="red--text">*</span></h2>
              <p class="mb-0">Fill in the details for your extra</p>
            </div>
          </v-col>
          <v-col cols="12">
            <h3 class="font-weight-regular pb-4 black--text">
              Name
              <span class="red--text">*</span>
            </h3>
            <v-text-field v-model="mutatableExtra.name" required outlined :rules="nameRules"> </v-text-field>
          </v-col>
          <v-col cols="12" class="pt-0">
            <h3 class="font-weight-regular pb-4 black--text">
              Description
              <span class="red--text">*</span>
            </h3>
            <v-textarea v-model="mutatableExtra.description" required outlined :rules="descriptionRules"> </v-textarea>
          </v-col>
          <v-col cols="12" class="pt-0">
            <h3 class="font-weight-regular pb-4 black--text">
              Type
              <span class="red--text">*</span>
            </h3>
            <v-select v-model="mutatableExtra.type" outlined :items="items('EXTRA_TYPES')"> </v-select>
          </v-col>
          <v-col cols="12" class="pt-0">
            <h3 class="font-weight-regular pb-4 black--text">
              Quantity
              <span class="red--text">*</span>
            </h3>
            <v-text-field v-model.number="mutatableExtra.max" class="maximum" type="number" min="1" outlined> </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h3 class="font-weight-regular pb-4 black--text">
              Pricing
              <span class="red--text">*</span>
            </h3>
            <p class="pb-4">Define how the extra should be calculated</p>
            <v-radio-group row v-model="mutatableExtra.priceType">
              <v-radio v-for="item in items('EXTRA_PRICE_TYPES')" :key="item.text" :value="item.value" :label="item.text" />
            </v-radio-group>
          </v-col>
        </v-row>

        <v-row v-show="mutatableExtra.priceType === 1" justify="space-between" no-gutters>
          <v-col class="field-group pt-0" cols="12">
            <v-text-field v-model.number="mutatableExtra.priceValue" class="pound mr-4" type="number" min="1" :rules="priceRules" outlined> </v-text-field>
            <v-select v-model="mutatableExtra.chargeType" class="mr-4" outlined :items="items('EXTRA_CHARGE_TYPES')"> </v-select>
            <v-select v-model="mutatableExtra.durationType" class="mr-4" outlined :items="items('EXTRA_DURATION_TYPES')"> </v-select>
          </v-col>
        </v-row>
        <v-row v-show="mutatableExtra.priceType === 2" justify="space-between" no-gutters>
          <v-col class="field-group pt-0" cols="12">
            <v-text-field v-model.number="mutatableExtra.priceValue" class="percentage mr-4" type="number" min="1" :rules="priceRules" outlined> </v-text-field>
            <v-select v-model="mutatableExtra.chargeType" class="mr-4" outlined :items="items('EXTRA_CHARGE_TYPES')"> </v-select>
            <v-select v-model="mutatableExtra.durationType" class="mr-4" outlined :items="items('EXTRA_DURATION_TYPES')"> </v-select>
          </v-col>
        </v-row>
        <v-row class="pt-12">
          <v-col cols="12" class="pt-0">
            <h2 class="font-weight-regular black--text pb-4">Select Places/Rooms</h2>
            <p class="mb-0">Which places/rooms would you like to apply the rates to?</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-autocomplete v-model="assignedRooms" :items="roomItems" outlined chips label="Search a room..." multiple></v-autocomplete>
          </v-col>
        </v-row>

        <v-alert v-if="!isValid" outlined type="warning" text class="mb-8 text-left">You cannot finalise this extra until all required fields have been populated.</v-alert>

        <div class="text-right">
          <v-btn :disabled="!isValid || saving" @click.stop="save()" right color="primary" large v-text="buttonText" :loading="saving" />
        </div>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import { useExtraStore, useUnitStore, useBookableStore } from '@/stores'
import { mapActions, mapState } from 'pinia'
import { EXTRA_PRICE_TYPES, EXTRA_TYPES, EXTRA_CHARGE_TYPES, EXTRA_DURATION_TYPES } from '@/constants'
import { clone } from 'ramda'

export default {
  props: {
    place: {
      type: Object,
      required: true,
    },
    isCreate: {
      type: Boolean,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: true,
    },
    isDuplicate: {
      type: Boolean,
      required: false,
      default: false,
    },
    extra: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      saving: false,
      assignedRooms: [],
      mutatableExtra: this.clone(this.extra),
      nameRules: [(v) => !!v || '*Name is required.'],
      descriptionRules: [(v) => !!v || '*Description is required.'],
      priceRules: [(v) => (v > 0 && !!v) || 'Price must be greater than 0'],
      maxRules: [(v) => (v > 0 && !!v) || 'Maximum must be greater than 0'],
    }
  },
  computed: {
    ...mapState(useUnitStore, {
      unitFindById: 'findById',
      unitsFindByIds: 'findByIds',
    }),
    ...mapState(useBookableStore, {
      bookablesFindByIds: 'findByIds',
    }),
    isValid () {
      return !!(this.mutatableExtra.name && this.mutatableExtra.description && this.mutatableExtra.priceValue && this.mutatableExtra.max)
    },
    buttonText () {
      let title
      if (this.isCreate && !this.isDuplicate) {
        title = 'Create Extra'
      } else if (this.isEdit) {
        title = 'Edit Extra'
      } else {
        title = 'Duplicate Extra'
      }

      return title
    },
    roomItems () {
      const placesRoomsItem = []
      this.bookablesFindByIds(this.place.bookables).map((bookable) => {
        placesRoomsItem.push({ header: bookable.name })
        this.unitsFindByIds(bookable.units).map((unit) => {
          placesRoomsItem.push({
            text: unit.name,
            value: unit['@id'],
          })
        })
      })
      return placesRoomsItem
    },
    units () {
      const units = []
      this.bookablesFindByIds(this.place.bookables).map((bookable) => {
        this.unitsFindByIds(bookable.units).map((unit) => {
          units.push(unit)
        })
      })
      return units
    },
  },
  methods: {
    clone,
    ...mapActions(useExtraStore, ['httpCreateExtra', 'httpUpdateExtra', 'httpDeleteExtra']),
    items (ID) {
      const lists = [
        { key: 'EXTRA_PRICE_TYPES', value: EXTRA_PRICE_TYPES },
        { key: 'EXTRA_TYPES', value: EXTRA_TYPES },
        { key: 'EXTRA_CHARGE_TYPES', value: EXTRA_CHARGE_TYPES },
        { key: 'EXTRA_DURATION_TYPES', value: EXTRA_DURATION_TYPES },
      ]
      const list = lists.filter((entry) => entry.key === ID)
      let items = []

      for (const key in list[0].value) {
        items.push({
          value: ID === 'EXTRA_TYPES' ? key : parseInt(key),
          text: list[0].value[key],
        })
      }

      return items
    },
    save () {
      if (this.$refs.extraForm.validate()) {
        this.saving = true

        const extraClone = this.clone(this.mutatableExtra)
        extraClone.units = this.assignedRooms

        if (this.isCreate) {
          return this.httpCreateExtra({ extra: extraClone, place: this.clone(this.place) }).then(() => {
            // redirect to manage extra page
            this.resetForm()

            this.$router.push({ name: 'place-extras-manage' })

            return Promise.resolve(true)
          })
        } else if (this.isEdit) {
          return this.httpUpdateExtra({ extra: extraClone })
            .then(() => {
              this.resetForm()

              this.$router.push({ name: 'place-extras-manage' })

              return Promise.resolve(true)
            })
            .catch(() => Promise.resolve(false))
        }
      }
    },
    resetForm () {
      this.$refs.extraForm.resetValidation()
      this.assignedRooms = []
    },
  },
  created () {
    // add existing ticked units
    if (this.isEdit || this.isDuplicate) {
      this.units.forEach((unit) => {
        if (unit && unit.extras.find((extra) => extra === this.extra.id || extra === this.extra.prevId)) {
          this.assignedRooms.push(unit['@id'])
        }
      })
    }
  },
  mounted () {
    if (this.isDuplicate) {
      this.mutatableExtra.name = `${this.mutatableExtra.name} (copy)`
    }
  },
  watch: {
    extra (newVal) {
      this.mutatableExtra = this.clone(newVal)
      if (this.isCreate) {
        this.resetForm()
      }
    },
  },
}
</script>

<style lang="scss">
.field-group {
  display: flex;
  align-items: baseline;

  .v-input {
    width: 75px;
    margin: 0 5px 0 5px;
  }

  .v-select {
    margin: 0 5px 0 5px;
    max-width: 250px;
  }
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }

  .pound,
  .percentage {
    max-width: 75px;
  }

  .pound {
    input {
      padding-left: 15px;
    }

    .v-text-field__slot {
      position: relative;

      &:before {
        position: absolute;
        left: -2.5px;
        top: 50%;
        transform: translate3d(0, -50%, 0);
        color: #b2b2b2;
        content: '£';
      }
    }
  }

  .percentage {
    input {
      padding-left: 5px;
    }

    .v-text-field__slot {
      position: relative;

      &:before {
        position: absolute;
        right: 2.5px;
        top: 50%;
        transform: translate3d(0, -50%, 0);
        color: #b2b2b2;
        content: '%';
      }
    }
  }

  .maximum {
    max-width: 150px;

    .v-text-field__slot {
      position: relative;

      &:before {
        position: absolute;
        right: 2.5px;
        top: 50%;
        transform: translate3d(0, -50%, 0);
        color: #b2b2b2;
        content: 'maximum';
      }
    }
  }
}
</style>
